import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useUserRbac, type ModuleAccessLevel } from '@/lib/rbacApi';
import { ADMIN_MODULES, adminModuleDefault } from '@/lib/roleAccessConfig';
import { normalizeRole } from '@/lib/roles';

/**
 * Effective admin-module access for the *current* user, for nav/route gating.
 *
 * System Admins resolve to full on every admin module (locked by role, no
 * fetch). Admins resolve from the Role & Access matrix defaults overlaid with
 * their per-user overrides (fetched once via the RBAC API). Member roles get
 * an empty map — they can hold no admin modules.
 */
export interface MyAdminModuleAccess {
  moduleAccess: Record<string, ModuleAccessLevel>;
  isLoading: boolean;
}

export function useMyAdminModuleAccess(): MyAdminModuleAccess {
  const { user } = useAuth();
  // Normalize like lib/roles helpers — AuthContext stores the API role verbatim.
  const role = normalizeRole(user?.role) || null;
  const isSystemAdmin = role === 'SYSTEM_ADMIN';
  const isAdmin = role === 'ADMIN';

  // Only plain Admins need their overrides; everyone else is static.
  const { data: myRbac, isLoading } = useUserRbac(isAdmin ? (user?.id ?? null) : null);

  return useMemo(() => {
    const moduleAccess: Record<string, ModuleAccessLevel> = {};
    if (isSystemAdmin || isAdmin) {
      for (const mod of ADMIN_MODULES) {
        moduleAccess[mod.key] = adminModuleDefault(role, mod.key).level;
      }
      if (isAdmin && myRbac) {
        for (const override of myRbac.overrides) {
          if (
            override.moduleKey in moduleAccess &&
            !adminModuleDefault(role, override.moduleKey).locked
          ) {
            moduleAccess[override.moduleKey] = override.accessLevel;
          }
        }
      }
    }
    return { moduleAccess, isLoading: isAdmin && isLoading };
  }, [role, isSystemAdmin, isAdmin, myRbac, isLoading]);
}
